import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import axios from 'axios';
import { useHistory, useLocation, useParams } from 'react-router';
import { ItemResponse, SaveResponse } from 'helpers/interfaces';

interface IContext {
    isNew: boolean
    isSaving: boolean
    isLoading: boolean,
    user: User

    saveUser: (ticket: User) => Promise<void>
}

export let userSchema = yup.object({
    id: yup.string().default(''),
    email: yup.string().email().default(""),
    firstName: yup.string().default('').label("First name")
        .when("email", {
            is: (email: string) => email?.includes("@manfrotto"),
            then: yup.string().default(''),
            otherwise: yup.string().default('').required()
        }),
    lastName: yup.string().default('').label("Last name")
        .when("email", {
            is: (email: string) => email?.includes("@manfrotto"),
            then: yup.string().default(''),
            otherwise: yup.string().default('').required()
        }),
    typology: yup.string().default("VitecUser"),
    groups: yup.array().of(yup.string())
        .when("canWorkOnTicketAssignedToMyGroup", {
            is: true,
            then: yup.array().of(yup.string()).min(1, "select atleast 1 gorup").default([]),
            otherwise: yup.array().of(yup.string()).default([])
        }).default([]),
    defaultSupplier: yup.string().default(""),
    canReplyToCustomer: yup.boolean().default(false),
    canCreateExcelTicketExport: yup.boolean().default(false),
    canWorkOnAllTicket: yup.boolean().default(false),
    canWorkOnTicketAssignedToMyGroup: yup.boolean().default(true),
    canManageCustomerUsers: yup.boolean().default(false),
    canManageVitecUsers: yup.boolean().default(false),
    canManageTicketSettings: yup.boolean().default(false),
    canViewTickets: yup.boolean().default(false),

    // notify
    receiveNewTicketNotifications: yup.boolean().default(false),
    receiveNewTicketNotificationsType: yup.string().oneOf(['all', 'bytype']).default('all'),
    newTicketNotificationsTicketTypes: yup.array().of(yup.string())
        .when('receiveNewTicketNotificationsType', {
            is: 'bytype',
            then: yup.array().default([]).min(1, 'At least one ticket type must be selected'),
            otherwise: yup.array().default([])
        }).default([]),

    notifyWhenImAssigned: yup.boolean().default(false),
    notifyResolutionsUpdate: yup.boolean().default(false),
    notifiyWhenMyGroupIsAdded: yup.boolean().default(false),

    canCreateTickets: yup.boolean().default(false),
    canDeleteTickets: yup.boolean().default(false),
    canDoBulkEdit: yup.boolean().default(false),
    claimsPlatformAdmin: yup.boolean().default(false),

    m3Id: yup.string().default('').nullable(),
    companyName: yup.string().default('').nullable(),
});

type User = yup.InferType<typeof userSchema>;

export let userContext = React.createContext({} as IContext);

let { Provider } = userContext;

let UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let history = useHistory();

    let { id } = useParams<{ id: string }>();
    let location = useLocation();

    let isNew = location.pathname.includes("/addnew");

    let [isSaving, setSaving] = useState(false);
    let [isLoading, setLoading] = useState<boolean>(isNew === false);
    let [user, setUser] = useState<User>(userSchema.getDefault())



    useEffect(() => {
        if (isNew === false)
            getUser();
    }, [id, isNew])

    let getUser = async () => {
        try {
            let res = await axios.get<ItemResponse<User>>('/api/User/GetUser', { params: { id } });

            let { item } = res.data;
            setUser({
                ...item,
                // notify
                newTicketNotificationsTicketTypes: item.newTicketNotificationsTicketTypes || [],
                notifyWhenImAssigned: item.notifyWhenImAssigned || false,
                notifyResolutionsUpdate: item.notifyResolutionsUpdate || false,
                notifiyWhenMyGroupIsAdded: item.notifiyWhenMyGroupIsAdded || false,

                defaultSupplier: item.defaultSupplier || '',
                groups: item.groups || [],
                claimsPlatformAdmin: item.claimsPlatformAdmin || false
            } as User);
            setLoading(false);

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let saveUser = async (user: User) => {
        try {
            setSaving(true);
            await axios.post<SaveResponse>('/api/User/SaveUser', {
                ...user,
                newTicketNotificationsTicketTypes: !user.receiveNewTicketNotifications ? [] : user.newTicketNotificationsTicketTypes
            } as User
            );
            history.push('/admin/users');
        } catch (err) {
            setSaving(false);
        }
    }


    return (
        <Provider
            value={{
                isNew,
                isSaving,
                user,
                isLoading,

                saveUser
            }}
        >
            {children}
        </Provider>
    )
}

export default UserProvider;